import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "./Primitives";
import { RiskBadge, StatusBadge } from "./Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Search, Plus, Trash2, Edit2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * A reusable registry/table page. Provides list + create + edit + delete + search.
 *
 * Props:
 * - title, description
 * - endpoint: string  (e.g. "/vendors")
 * - testIdPrefix: e.g. "vendors"
 * - columns: [{ key, label, render?, width? }]
 * - fields:  [{ key, label, type: "text"|"textarea"|"select"|"checkbox"|"number", options?, required? }]
 * - defaultItem: {}
 * - actions?: (item, refresh) => ReactNode  (optional per-row action buttons)
 * - extraHeaderActions?: ReactNode
 * - reportKey?: string (if set, shows Export PDF button)
 */
export default function RegistryPage({
  title, description, endpoint, testIdPrefix,
  columns, fields, defaultItem = {}, actions, extraHeaderActions, reportKey,
}) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultItem);
  const [saving, setSaving] = useState(false);

  const load = async (p = page, search = q) => {
    setItems(null);
    try {
      const { data } = await api.get(endpoint, { params: { page: p, limit, search } });
      // Backwards compat: some endpoints return array, others return {items,total,page,limit}
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      toast.error("Failed to load");
      setItems([]);
      setTotal(0);
    }
  };
  useEffect(() => { load(1, q); setPage(1);   }, [endpoint]);

  // Debounced re-fetch when search changes
  useEffect(() => {
    const t = setTimeout(() => { load(1, q); setPage(1); }, 350);
    return () => clearTimeout(t);
     
  }, [q]);

  const filtered = items || [];

  const openCreate = () => { setEditing(null); setForm({ ...defaultItem }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...defaultItem, ...item }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = { ...form };
      // coerce array fields defined by comma if user provided strings
      Object.keys(cleaned).forEach((k) => {
        if (Array.isArray(defaultItem[k]) && typeof cleaned[k] === "string") {
          cleaned[k] = cleaned[k].split(",").map((v) => v.trim()).filter(Boolean);
        }
        if (typeof defaultItem[k] === "number" && cleaned[k] !== null && cleaned[k] !== undefined && cleaned[k] !== "") {
          const n = Number(cleaned[k]); if (!Number.isNaN(n)) cleaned[k] = n;
        }
      });
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, cleaned);
        toast.success("Updated");
      } else {
        await api.post(endpoint, cleaned);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportReport = async () => {
    try {
      const res = await api.get(`/reports/${reportKey}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${reportKey}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const renderInput = (f) => {
    const val = form[f.key] ?? (Array.isArray(defaultItem[f.key]) ? "" : defaultItem[f.key] ?? "");
    const set = (v) => setForm((s) => ({ ...s, [f.key]: v }));
    const displayVal = Array.isArray(val) ? val.join(", ") : val;
    if (f.type === "textarea") return <TextArea data-testid={`field-${f.key}`} value={displayVal || ""} onChange={(e) => set(e.target.value)} />;
    if (f.type === "select") return (
      <SelectInput data-testid={`field-${f.key}`} value={val ?? ""} onChange={(e) => set(e.target.value)}>
        <option value="">-- Select --</option>
        {(f.options || []).map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </SelectInput>
    );
    if (f.type === "checkbox") return (
      <label className="flex items-center gap-2 text-sm">
        <input data-testid={`field-${f.key}`} type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} />
        <span className="text-slate-700">{f.checkboxLabel || "Yes"}</span>
      </label>
    );
    if (f.type === "number") return <TextInput data-testid={`field-${f.key}`} type="number" value={val ?? ""} onChange={(e) => set(e.target.value)} />;
    return <TextInput data-testid={`field-${f.key}`} value={displayVal || ""} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <div>
      <PageHeader
        title={title}
        description={description}
        testId={`${testIdPrefix}-page-header`}
        actions={
          <>
            {extraHeaderActions}
            {reportKey && (
              <Btn data-testid={`${testIdPrefix}-export-btn`} variant="secondary" onClick={exportReport}>
                <Download className="w-4 h-4" /> Export PDF
              </Btn>
            )}
            {fields && fields.length > 0 && (
              <Btn data-testid={`${testIdPrefix}-create-btn`} onClick={openCreate}>
                <Plus className="w-4 h-4" /> New
              </Btn>
            )}
          </>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <TextInput
            data-testid={`${testIdPrefix}-search`}
            className="pl-9"
            placeholder="Search..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">
          {total} record{total === 1 ? "" : "s"} · page {page}/{Math.max(1, Math.ceil(total / limit))}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            data-testid={`${testIdPrefix}-page-prev`}
            disabled={page <= 1}
            onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p, q); }}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg surface disabled:opacity-40 transition-all hover:card-shadow"
            style={{ border: "1px solid var(--border)" }}
          >
            ← Prev
          </button>
          <button
            data-testid={`${testIdPrefix}-page-next`}
            disabled={page >= Math.ceil(total / limit)}
            onClick={() => { const p = page + 1; setPage(p); load(p, q); }}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg surface disabled:opacity-40 transition-all hover:card-shadow"
            style={{ border: "1px solid var(--border)" }}
          >
            Next →
          </button>
        </div>
      </div>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState title="Nothing here yet" hint="Click 'New' to create your first entry." />
      ) : (
        <div className="surface rounded-xl overflow-hidden card-shadow" style={{ border: "1px solid var(--border)" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider muted" style={{ width: c.width }}>
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider muted">Actions</th>
                </tr>
              </thead>
              <tbody data-testid={`${testIdPrefix}-table-body`}>
                {filtered.map((it) => (
                  <tr key={it.id} className="transition-colors hover:bg-app-2" style={{ borderBottom: "1px solid var(--border)" }}>
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 ink align-middle">
                        {c.render ? c.render(it, load) : (
                          typeof it[c.key] === "boolean" ? (it[c.key] ? "Yes" : "No") :
                          Array.isArray(it[c.key]) ? it[c.key].join(", ") :
                          (it[c.key] ?? "—")
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right whitespace-nowrap align-middle">
                      {actions && actions(it, load)}
                      {fields && fields.length > 0 && (
                        <>
                          <button data-testid={`${testIdPrefix}-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-md muted hover:ink hover:bg-app transition-all" title="Edit">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`${testIdPrefix}-delete-${it.id}`} onClick={() => del(it)} className="p-1.5 rounded-md muted hover:danger hover:bg-app transition-all" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Create"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
            {(fields || []).map((f) => (
              <Field key={f.key} label={f.label} required={f.required} className={f.full ? "sm:col-span-2" : ""}>
                {renderInput(f)}
              </Field>
            ))}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)} data-testid={`${testIdPrefix}-cancel-btn`}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} data-testid={`${testIdPrefix}-save-btn`}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { RegistryPage };
